/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'src/utils/notification';
import { useDispatch, useSelector } from 'react-redux';
import { setWorkspaceGroup, setWorkspaceGroups } from 'src/store/workspaces/workspaces/actions';
import { predefinedColors, predefinedIcons } from '../../settings/settings';
import { WorkspaceService } from 'src/api/services/workspaces/Service.workspace';
import { TabService } from 'src/api/services/workspaces/Service.tab';
import { setTabEnabled } from '../utils/enableTab';

const CreateWorkspaceDialog = ({ visible, onClose, workspaceIndex, setShowDialog }) => {
  const [newGroupName, setNewGroupName] = React.useState('');
  const [groupColor, setGroupColor] = React.useState(predefinedColors[0]);
  const [selectedGroupName, setSelectedGroupName] = React.useState('');
  const [useIcon, setUseIcon] = React.useState(false);
  const [icon, setIcon] = React.useState(predefinedIcons[0]);

  const workspaces = useSelector((state: any) => state.workspaces);
  const dispatch = useDispatch<any>();
  const tabService = new TabService();
  const workspaceService = new WorkspaceService();

  useEffect(() => {
    const localGroup = JSON.parse(localStorage.getItem('activeWorkspace') || '{}');
    if (localGroup?.name) setSelectedGroupName(localGroup.name);
  }, []);


  const handleDialogOk = async () => {
  
    if (newGroupName.trim() === '' && selectedGroupName.trim() !== '') {
      const group = workspaces.groups.find(g => g.name === selectedGroupName);
      const currentTab = workspaces.instances[workspaceIndex];
      group.tabs = [...(group.tabs || []), currentTab?.uuid];

      await workspaceService.update(group);
      await tabService.deleteTab(currentTab.uuid);
      await tabService.setTab(currentTab, group);
      setTabEnabled(currentTab, group, false);
      handleClose();
      setShowDialog(false);
      return;
    }

    if (newGroupName.trim() !== '') {
      const name = newGroupName.trim() || selectedGroupName;

      if (!name) return toast.warning('Name erforderlich');
      if (workspaces.groups.find((g) => g.name === name)) return toast.warning('Name existiert bereits');

      const currentTab = workspaces.instances[workspaceIndex];
      const group = { uuid: uuidv4(), name, color: groupColor, icon: useIcon ? icon : '', tabs: [currentTab.uuid], locked: false };

      await dispatch(setWorkspaceGroup(workspaceIndex, group));
      await workspaceService.setGroup(group);
      await tabService.deleteTab(currentTab.uuid);
      await tabService.setTab(currentTab, group);
      const res = await workspaceService.getAll();
      dispatch(setWorkspaceGroups(res));
      setTabEnabled(currentTab, group, false);
      handleClose();
      setShowDialog(false);
    }
  };

  const handleClose = () => { setNewGroupName('');onClose();};

  const dialogButtons = [
    ...(workspaces.groups.length ? [{ buttonModel: { content: 'Abbrechen', cssClass: 'e-custom modal-dialog-btn' }, click: handleClose }] : []),
    { buttonModel: { content: 'Ok', isPrimary: true, cssClass: 'e-custom modal-dialog-btn' }, click: handleDialogOk }
  ];

  return (
    <DialogComponent
      visible={visible}
      target="#root"
      width="340px"
      header="Workspace erstellen"
      showCloseIcon={workspaces.groups.length !== 0}
      isModal={true}
      close={handleClose}
      zIndex={2000}
      buttons={dialogButtons}
      allowDragging={false}
    >
      <div className="px-4">
        {workspaces.groups.length === 0 && (
          <label htmlFor="groupNameInput" className="block mb-1 text-sm text-gray-700">
            Erstellen Sie einen erste Workspace, um Tabs zu aktivieren. Geben Sie dafür einen Namen ein.
          </label>
        )}
        {workspaces.groups.length > 1 && (
          <div className="mb-3">
            <label className="block text-sm text-gray-700 mb-1">Workspace auswählen</label>
            <DropDownListComponent
              dataSource={workspaces.groups.filter(group => group.uuid !== JSON.parse(localStorage.getItem('activeWorkspace'))?.uuid)}
              fields={{ text: 'name', value: 'name' }}
              value={selectedGroupName}
              change={(e) => setSelectedGroupName(e.value)}
              enabled={newGroupName.trim() === ''}
            />
          </div>
        )}

        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">Name des neuen Workspaces</label>
          <TextBoxComponent
            id="groupNameInput"
            value={newGroupName}
            input={(e) => setNewGroupName(e.value)}
          />
        </div>

        <div className={`transition-all duration-300 ${newGroupName ? 'visible opacity-100 h-auto mb-4' : 'invisible opacity-0 h-0 overflow-hidden'}`}>
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              {predefinedColors.map((color) => (
                <div
                  key={color}
                  onClick={() => setGroupColor(color)}
                  className={`w-6 h-6 rounded-full cursor-pointer border transition-all duration-200 ${
                    groupColor === color ? 'border-gray-700 ring-1 ring-gray-700' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                ></div>
              ))}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm text-gray-700 mt-2">
            <input
              type="checkbox"
              checked={useIcon}
              onChange={() => {
                setUseIcon(!useIcon);
                if (!useIcon && !icon) setIcon(predefinedIcons[0]);
              }}
            />
            Icon verwenden
          </label>

          <div className={`${useIcon ? 'block' : 'hidden'} flex flex-wrap gap-2 max-h-[250px] overflow-y-auto mt-2`}>
            {predefinedIcons.map((i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIcon(i)}
                className={` rounded w-8 h-8 flex items-center justify-center transition border ${
                  icon === i ? 'border-gray-700 bg-gray-100 shadow-inner' : 'border-transparent'
                }`}
                title={i}
              >
                <span className={`e-icons e-${i} text-xl text-gray-700`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </DialogComponent>
  );
};

CreateWorkspaceDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workspaceIndex: PropTypes.any,
  setShowDialog: PropTypes.func.isRequired
};

export default CreateWorkspaceDialog;
